import type { DefLookup } from "../catalog";
import { effectiveTerminals } from "../effective";
import { indexNodesById } from "../node-tree";
import type { System } from "../types";

export type CableEndpointStatus = "valid" | "missing-node" | "missing-terminal" | "unknown-definition";

/** A missing catalog definition is uncertain, not evidence that its terminals
 * were removed. Keep that distinction so existing verification notices survive. */
export function cableEndpointStatus(
  system: System,
  lookup: DefLookup,
): (nodeId: string, terminalId: string) => CableEndpointStatus {
  const nodes = indexNodesById(system.nodes);
  const terminals = new Map<string, Set<string> | undefined>();
  return (nodeId, terminalId) => {
    const node = nodes[nodeId];
    if (node?.kind !== "component") return "missing-node";
    if (!terminals.has(nodeId)) {
      const def = lookup(node.definitionId, node.customDefinition);
      terminals.set(nodeId, def ? new Set(effectiveTerminals(def, node).map(t => t.id)) : undefined);
    }
    const ids = terminals.get(nodeId);
    return ids == null ? "unknown-definition" : ids.has(terminalId) ? "valid" : "missing-terminal";
  };
}

const views = new WeakMap<System, WeakMap<DefLookup, System>>();

/** An electrical view, never a repair of the saved drawing. Proven dangling
 * ends cannot join circuits or count as additional connections. Keep the raw
 * cables for the endpoint diagnostic and explicit, undoable user corrections. */
export function withoutDanglingCables(system: System, lookup: DefLookup): System {
  let byLookup = views.get(system);
  if (!byLookup) {
    byLookup = new WeakMap();
    views.set(system, byLookup);
  }
  const cached = byLookup.get(lookup);
  if (cached) return cached;
  const status = cableEndpointStatus(system, lookup);
  const exists = (nodeId: string, terminalId: string) => {
    const result = status(nodeId, terminalId);
    return result === "valid" || result === "unknown-definition";
  };
  const cables = system.cables.filter(c =>
    exists(c.fromNodeId, c.fromTerminalId) && exists(c.toNodeId, c.toTerminalId));
  const result = cables.length === system.cables.length ? system : { ...system, cables };
  byLookup.set(lookup, result);
  return result;
}
